import type { LevelIdentity } from '../model/identity';
import type {
  DifficultyBand,
  DirectionHeuristicWeights,
  LevelRecipe,
  PlacementStrategyId,
  PuzzleArchetype,
  StencilRotation,
} from '../model/recipe';
import { getTuning, type BandConfig, type TuningConfig } from './config/tuning';
import { createMask, type Mask } from './stencil/mask';
import {
  eligibleStencils,
  FALLBACK_STENCIL_ID,
  requireStencil,
  type StencilManifestEntry,
} from './stencil/manifest';
import { processStencil } from './stencil/process';
import { stencilCoverage, stencilSolidity } from './stencil/solidity';
import { deriveRootSeed, rngFor, SUBSYSTEM } from './seeds';
import { Pcg32 } from './prng';
import { MINIMUM_ARROW_LENGTH } from './reverseConstruction';

/**
 * The LevelDirector turns a LevelIdentity into a LevelRecipe: the single place where
 * campaign pacing, board size, silhouette choice and archetype are decided.
 *
 * Everything is a pure function of the identity. Difficulty deliberately does not increase
 * monotonically — a repeating wave, periodic breather levels and periodic showcase levels
 * keep the campaign from becoming a straight ramp.
 */

export interface LevelPlan {
  readonly recipe: LevelRecipe;
  readonly mask: Mask;
  readonly activeCells: number;
  /** Cells the generator should aim to cover with arrow paths. */
  readonly targetOccupiedCells: number;
  /** Stencils tried before one validated, oldest first. Surfaced in the developer lab. */
  readonly stencilAttempts: readonly string[];
}

interface ProgressionStage {
  readonly maxLevel: number;
  readonly bands: readonly DifficultyBand[];
}

const PROGRESSION: readonly ProgressionStage[] = [
  { maxLevel: 20, bands: ['gentle', 'gentle', 'gentle', 'easy'] },
  { maxLevel: 60, bands: ['easy', 'medium', 'easy', 'easy', 'medium'] },
  { maxLevel: 150, bands: ['medium', 'hard', 'medium', 'medium', 'hard', 'medium'] },
  {
    maxLevel: Number.MAX_SAFE_INTEGER,
    bands: ['hard', 'expert', 'medium', 'hard', 'expert', 'hard'],
  },
];

const TUTORIAL_LEVEL_COUNT = 5;

const BAND_ORDER: readonly DifficultyBand[] = [
  'tutorial',
  'gentle',
  'easy',
  'medium',
  'hard',
  'expert',
];

const ARCHETYPE_STRATEGIES: Readonly<Record<PuzzleArchetype, readonly PlacementStrategyId[]>> = {
  'broad-branching': ['uniform', 'frontier', 'center-out'],
  'narrow-chain': ['depth-biased', 'corridor', 'frontier'],
  'island-regions': ['sparse-islands', 'component-aware', 'uniform'],
  'dense-center': ['center-out', 'frontier', 'depth-biased'],
  'sparse-center': ['perimeter-in', 'sparse-islands', 'uniform'],
  corridor: ['corridor', 'depth-biased', 'frontier'],
  perimeter: ['perimeter-in', 'component-aware', 'uniform'],
  'near-symmetric': ['near-symmetric', 'center-out', 'uniform'],
  asymmetric: ['frontier', 'uniform', 'corridor'],
  'silhouette-showcase': ['uniform', 'center-out', 'component-aware'],
};

const ARCHETYPE_DIRECTION_WEIGHTS: Readonly<Record<PuzzleArchetype, DirectionHeuristicWeights>> = {
  'broad-branching': {
    base: 10,
    blocksExisting: 2,
    createsDependency: 3,
    edgeDistance: 2,
    directionBalance: 6,
    localRepetition: 6,
    towardsInterior: 1,
  },
  'narrow-chain': {
    base: 6,
    blocksExisting: 9,
    createsDependency: 9,
    edgeDistance: 4,
    directionBalance: 3,
    localRepetition: 4,
    towardsInterior: 5,
  },
  'island-regions': {
    base: 10,
    blocksExisting: 3,
    createsDependency: 4,
    edgeDistance: 2,
    directionBalance: 5,
    localRepetition: 7,
    towardsInterior: 1,
  },
  'dense-center': {
    base: 8,
    blocksExisting: 6,
    createsDependency: 6,
    edgeDistance: 3,
    directionBalance: 4,
    localRepetition: 5,
    towardsInterior: 4,
  },
  'sparse-center': {
    base: 10,
    blocksExisting: 3,
    createsDependency: 3,
    edgeDistance: 5,
    directionBalance: 5,
    localRepetition: 6,
    towardsInterior: 0,
  },
  corridor: {
    base: 7,
    blocksExisting: 7,
    createsDependency: 7,
    edgeDistance: 6,
    directionBalance: 3,
    localRepetition: 3,
    towardsInterior: 3,
  },
  perimeter: {
    base: 9,
    blocksExisting: 4,
    createsDependency: 4,
    edgeDistance: 6,
    directionBalance: 5,
    localRepetition: 6,
    towardsInterior: 0,
  },
  'near-symmetric': {
    base: 9,
    blocksExisting: 5,
    createsDependency: 5,
    edgeDistance: 3,
    directionBalance: 8,
    localRepetition: 5,
    towardsInterior: 2,
  },
  asymmetric: {
    base: 9,
    blocksExisting: 5,
    createsDependency: 6,
    edgeDistance: 3,
    directionBalance: 2,
    localRepetition: 5,
    towardsInterior: 2,
  },
  'silhouette-showcase': {
    base: 11,
    blocksExisting: 3,
    createsDependency: 3,
    edgeDistance: 2,
    directionBalance: 7,
    localRepetition: 8,
    towardsInterior: 1,
  },
};

/** Archetype-specific adjustment to arrow path length. */
const ARCHETYPE_LENGTH_BONUS: Readonly<Record<PuzzleArchetype, number>> = {
  'broad-branching': -1,
  'narrow-chain': 2,
  'island-regions': 0,
  'dense-center': 1,
  'sparse-center': -1,
  corridor: 2,
  perimeter: 0,
  'near-symmetric': 0,
  asymmetric: 1,
  'silhouette-showcase': -1,
};

/** Archetype-specific adjustment to how eagerly paths cross existing corridors. */
const ARCHETYPE_BLOCKING_BONUS: Readonly<Record<PuzzleArchetype, number>> = {
  'broad-branching': -1,
  'narrow-chain': 2,
  'island-regions': -1,
  'dense-center': 1,
  'sparse-center': 0,
  corridor: 1,
  perimeter: 0,
  'near-symmetric': 0,
  asymmetric: 1,
  'silhouette-showcase': -1,
};

/** Archetype-specific adjustment to how often a tail turns a corner. */
const ARCHETYPE_TURN_BONUS: Readonly<Record<PuzzleArchetype, number>> = {
  'broad-branching': 0,
  'narrow-chain': 1,
  'island-regions': 0,
  'dense-center': 2,
  'sparse-center': -1,
  corridor: 2,
  perimeter: 1,
  'near-symmetric': 0,
  asymmetric: 1,
  'silhouette-showcase': -1,
};

export function isTutorialLevel(levelNumber: number): boolean {
  return levelNumber <= TUTORIAL_LEVEL_COUNT;
}

export function tutorialLevelCount(): number {
  return TUTORIAL_LEVEL_COUNT;
}

/** Difficulty band for a level, including breather relief. */
export function bandForLevel(levelNumber: number, tuning: TuningConfig): DifficultyBand {
  if (isTutorialLevel(levelNumber)) return 'tutorial';

  const stage =
    PROGRESSION.find((entry) => levelNumber <= entry.maxLevel) ??
    PROGRESSION[PROGRESSION.length - 1];
  let band = stage.bands[levelNumber % stage.bands.length];

  if (levelNumber % tuning.breatherInterval === 0) {
    band = stepBand(band, -1);
  }
  return band;
}

function stepBand(band: DifficultyBand, delta: number): DifficultyBand {
  const index = BAND_ORDER.indexOf(band);
  // Never step down into the tutorial band: it is reserved for hand-authored levels.
  const next = Math.min(Math.max(index + delta, 1), BAND_ORDER.length - 1);
  return BAND_ORDER[next];
}

/** Cyclic modifier in permille applied to occupancy and size within a band. */
function waveModifier(levelNumber: number, tuning: TuningConfig): number {
  return tuning.progressionWave[levelNumber % tuning.progressionWave.length];
}

function isShowcaseLevel(levelNumber: number, tuning: TuningConfig): boolean {
  return levelNumber > TUTORIAL_LEVEL_COUNT && levelNumber % tuning.showcaseInterval === 0;
}

interface Dimensions {
  readonly width: number;
  readonly height: number;
}

function planDimensions(
  levelNumber: number,
  band: BandConfig,
  tuning: TuningConfig,
  rng: Pcg32,
): Dimensions {
  const wave = waveModifier(levelNumber, tuning);
  const widthSpan = band.maxWidth - band.minWidth;
  const heightSpan = band.maxHeight - band.minHeight;

  // The wave biases towards the smaller or larger end of the band without ever leaving it.
  const widthBias = Math.round((widthSpan * wave) / 1000);
  const heightBias = Math.round((heightSpan * wave) / 1000);

  const width = clampInt(
    band.minWidth + widthBias + rng.nextInt(0, Math.max(0, widthSpan - Math.abs(widthBias))),
    band.minWidth,
    band.maxWidth,
  );
  const height = clampInt(
    band.minHeight + heightBias + rng.nextInt(0, Math.max(0, heightSpan - Math.abs(heightBias))),
    band.minHeight,
    band.maxHeight,
  );
  return { width, height };
}

function clampInt(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Selection weight for a stencil.
 *
 * Solidity dominates, because it decides whether a board plays as a puzzle or as a list of
 * independent taps: a solid silhouette packs arrows into shared rows and columns, where the
 * blocking happens. Showcase levels lift recognisable icons so the campaign still shows a
 * silhouette from time to time, but never below the band's solidity floor.
 */
function stencilWeight(entry: StencilManifestEntry, band: BandConfig, showcase: boolean): number {
  const complexity = entry.complexityRating ?? 3;
  if (complexity > band.maximumStencilComplexity) return 0;
  if (!band.stencilCategories.includes(entry.category)) return 0;

  const solidity = stencilSolidity(entry);
  if (solidity < band.minimumStencilSolidity) return 0;

  // Cubed above the floor: the most solid silhouettes end up an order of magnitude more
  // likely than the ones that only just qualify.
  const headroom = solidity - band.minimumStencilSolidity + 0.04;
  let weight = Math.max(1, Math.round(headroom * headroom * headroom * 6000));

  // Coverage matters as much as solidity: a diamond is solid but leaves half the grid
  // unused, which costs the level the arrows that would have gone there.
  const coverage = stencilCoverage(entry);
  weight = Math.max(1, Math.round(weight * coverage * coverage));

  if (showcase && entry.category === 'icon') weight *= 6;
  return weight;
}

/**
 * Stencil choice without recency filtering. Kept separate so a level's history can be
 * recomputed in O(window) without recursion.
 */
function pickStencilRaw(identity: LevelIdentity, tuning: TuningConfig): string {
  const levelNumber = identity.levelNumber;
  if (levelNumber < 1) return FALLBACK_STENCIL_ID;

  const band = tuning.bands[bandForLevel(levelNumber, tuning)];
  const rootSeed = deriveRootSeed({ ...identity, levelNumber });
  const dimensions = planDimensions(
    levelNumber,
    band,
    tuning,
    new Pcg32(rootSeed ^ 0x9e3779b97f4a7c15n),
  );
  const showcase = isShowcaseLevel(levelNumber, tuning);
  const candidates = eligibleStencils(
    dimensions.width,
    dimensions.height,
    identity.contentVersion,
    {
      categories: band.stencilCategories,
      minimumSolidity: band.minimumStencilSolidity,
    },
  );
  if (candidates.length === 0) return FALLBACK_STENCIL_ID;

  const weights = candidates.map((entry) => stencilWeight(entry, band, showcase));
  const rng = rngFor(rootSeed, SUBSYSTEM.stencilSelection);
  return candidates[rng.weightedIndex(weights)].id;
}

/**
 * Predicts which stencil a level will actually use, without processing any masks.
 *
 * The preferred pick is often unavailable — it may be excluded by that level's own recency
 * set — in which case the level falls through to its weighted permutation. Comparing against
 * the *preferred* pick alone therefore misses most real repeats, which is what let the same
 * silhouette appear twice in a row. This prediction is right whenever the chosen stencil
 * validates at its grid size, which is the overwhelmingly common case.
 */
function predictStencilChoice(identity: LevelIdentity, tuning: TuningConfig): string {
  const levelNumber = identity.levelNumber;
  if (levelNumber <= TUTORIAL_LEVEL_COUNT) return FALLBACK_STENCIL_ID;

  const band = tuning.bands[bandForLevel(levelNumber, tuning)];
  const rootSeed = deriveRootSeed(identity);
  const dimensions = planDimensions(
    levelNumber,
    band,
    tuning,
    new Pcg32(rootSeed ^ 0x9e3779b97f4a7c15n),
  );
  const eligible = eligibleStencils(dimensions.width, dimensions.height, identity.contentVersion, {
    categories: band.stencilCategories,
    minimumSolidity: band.minimumStencilSolidity,
  });
  if (eligible.length === 0) return FALLBACK_STENCIL_ID;

  // One level of history only: deeper recursion would mean replaying the campaign.
  const recent = new Set<string>();
  for (let offset = 1; offset <= tuning.stencilRecencyWindow; offset += 1) {
    const previous = levelNumber - offset;
    if (previous <= TUTORIAL_LEVEL_COUNT) break;
    recent.add(pickStencilRaw({ ...identity, levelNumber: previous }, tuning));
  }

  const ordered = orderStencilCandidates(
    eligible,
    pickStencilRaw(identity, tuning),
    recent,
    band,
    isShowcaseLevel(levelNumber, tuning),
    rngFor(rootSeed, SUBSYSTEM.stencilSelection, 1),
  );
  return ordered[0]?.id ?? FALLBACK_STENCIL_ID;
}

/** What the previous levels *preferred*, which decides this level's candidate pool. */
function recentStencilIds(identity: LevelIdentity, tuning: TuningConfig): Set<string> {
  const recent = new Set<string>();

  for (let offset = 1; offset <= tuning.stencilRecencyWindow; offset += 1) {
    const previous = identity.levelNumber - offset;
    if (previous <= TUTORIAL_LEVEL_COUNT) break;
    recent.add(pickStencilRaw({ ...identity, levelNumber: previous }, tuning));
  }
  return recent;
}

/**
 * Silhouettes the immediate neighbours are predicted to use.
 *
 * Kept out of the recency set on purpose. The recency set decides the candidate *pool*, and
 * the predictor models a level built from that pool — feed predictions back into it and the
 * predictor stops describing the thing it is predicting. Instead these are applied at the
 * very end, demoting a collision to the back of an otherwise unchanged order.
 */
function neighbourChoices(identity: LevelIdentity, tuning: TuningConfig): Set<string> {
  const choices = new Set<string>();
  for (let offset = 1; offset <= PREDICTED_NEIGHBOURS; offset += 1) {
    const previous = identity.levelNumber - offset;
    if (previous <= TUTORIAL_LEVEL_COUNT) break;
    choices.add(predictStencilChoice({ ...identity, levelNumber: previous }, tuning));
  }
  return choices;
}

/**
 * How many immediate neighbours are checked for a collision.
 *
 * Two measures best, and more is worse: the prediction models a level that was *not* itself
 * demoted, so every extra neighbour demoted is another level the prediction gets wrong.
 * Measured immediate-repeat rate: 2.4% at two, 3.9% at three, 5.9% at six.
 */
const PREDICTED_NEIGHBOURS = 2;

function pickArchetypeRaw(
  levelNumber: number,
  band: BandConfig,
  tuning: TuningConfig,
  rootSeed: bigint,
): PuzzleArchetype {
  if (isShowcaseLevel(levelNumber, tuning)) return 'silhouette-showcase';
  const rng = rngFor(rootSeed, SUBSYSTEM.archetype);
  return rng.pick(band.archetypes);
}

/**
 * Produces the plan for a level: recipe plus the processed stencil mask.
 * Stencil processing failures fall through to the next eligible stencil and finally to a
 * plain rectangle, so a malformed stencil can never make a level ungenerable.
 */
export function planLevel(identity: LevelIdentity): LevelPlan {
  const tuning = getTuning(identity.contentVersion);
  const bandName = bandForLevel(identity.levelNumber, tuning);
  const band = tuning.bands[bandName];
  const rootSeed = deriveRootSeed(identity);
  const showcase = isShowcaseLevel(identity.levelNumber, tuning);

  const dimensions = planDimensions(
    identity.levelNumber,
    band,
    tuning,
    new Pcg32(rootSeed ^ 0x9e3779b97f4a7c15n),
  );

  const preferredId = pickStencilRaw(identity, tuning);
  const recent = recentStencilIds(identity, tuning);
  const eligible = eligibleStencils(dimensions.width, dimensions.height, identity.contentVersion, {
    categories: band.stencilCategories,
    minimumSolidity: band.minimumStencilSolidity,
  });

  const ordered = demoteNeighbourChoices(
    orderStencilCandidates(
      eligible,
      preferredId,
      recent,
      band,
      showcase,
      rngFor(rootSeed, SUBSYSTEM.stencilSelection, 1),
    ),
    neighbourChoices(identity, tuning),
  );
  const transformRng = rngFor(rootSeed, SUBSYSTEM.stencilTransform);

  const attempts: string[] = [];
  let chosen: StencilManifestEntry | undefined;
  let mask: Mask | undefined;
  let rotation: StencilRotation = 0;
  let mirrorHorizontal = false;
  let mirrorVertical = false;

  for (const entry of ordered) {
    attempts.push(entry.id);
    const candidateRotation = transformRng.pick(entry.allowedRotations);
    const candidateMirrorH = entry.allowHorizontalMirror && transformRng.nextBool();
    const candidateMirrorV = entry.allowVerticalMirror && transformRng.nextBool();
    const result = processStencil(entry, {
      gridWidth: dimensions.width,
      gridHeight: dimensions.height,
      rotation: candidateRotation,
      mirrorHorizontal: candidateMirrorH,
      mirrorVertical: candidateMirrorV,
    });
    if (result.ok) {
      chosen = entry;
      mask = result.mask;
      rotation = candidateRotation;
      mirrorHorizontal = candidateMirrorH;
      mirrorVertical = candidateMirrorV;
      break;
    }
    if (attempts.length >= 8) break;
  }

  if (chosen === undefined || mask === undefined) {
    // Last resort: a full rectangle always validates, at any grid size.
    chosen = requireStencil(FALLBACK_STENCIL_ID);
    attempts.push(`${FALLBACK_STENCIL_ID}:forced`);
    mask = filledMask(dimensions.width, dimensions.height);
    rotation = 0;
    mirrorHorizontal = false;
    mirrorVertical = false;
  }

  let activeCells = 0;
  for (let i = 0; i < mask.cells.length; i += 1) activeCells += mask.cells[i];

  const recipeRng = rngFor(rootSeed, SUBSYSTEM.recipe);
  const archetype = pickArchetypeRaw(identity.levelNumber, band, tuning, rootSeed);
  const strategy = pickStrategy(archetype, band, recipeRng);

  const wave = waveModifier(identity.levelNumber, tuning);
  const occupancySpan = band.occupancyPermille[1] - band.occupancyPermille[0];
  const occupancyBase =
    band.occupancyPermille[0] + recipeRng.nextInt(0, Math.max(0, occupancySpan));
  const occupancyPermille = clampInt(
    occupancyBase + Math.round((occupancySpan * wave) / 2000) - (showcase ? 60 : 0),
    band.occupancyPermille[0] - 40,
    band.occupancyPermille[1] + 40,
  );

  // Occupancy is measured in covered cells; the arrow count follows from the mean path
  // length, because a board of long arrows needs far fewer of them to feel as full.
  const targetOccupiedCells = clampInt(
    Math.round((activeCells * occupancyPermille) / 1000),
    Math.min(8, activeCells),
    Math.min(activeCells, tuning.maxOccupiedCells),
  );
  const meanLength = (band.arrowLengthRange[0] + band.arrowLengthRange[1]) / 2;
  const targetArrowCount = clampInt(
    Math.round(targetOccupiedCells / Math.max(1, meanLength)),
    1,
    tuning.maxArrowsPerLevel,
  );

  const depthSpan = band.dependencyDepthRange[1] - band.dependencyDepthRange[0];
  const targetDependencyDepth =
    band.dependencyDepthRange[0] + recipeRng.nextInt(0, Math.max(0, depthSpan));

  const directionHeuristics = jitterWeights(ARCHETYPE_DIRECTION_WEIGHTS[archetype], recipeRng);
  // Longer, twistier arrows on archetypes that want dependency structure.
  const lengthLow = clampInt(
    band.arrowLengthRange[0] + ARCHETYPE_LENGTH_BONUS[archetype],
    MINIMUM_ARROW_LENGTH,
    band.arrowLengthRange[1],
  );
  const lengthHigh = clampInt(
    band.arrowLengthRange[1] + ARCHETYPE_LENGTH_BONUS[archetype],
    lengthLow,
    24,
  );
  const arrowLengthRange: readonly [number, number] = [lengthLow, lengthHigh];
  const turnBias = clampInt(band.turnBias + ARCHETYPE_TURN_BONUS[archetype], 1, 8);
  const blockingBias = clampInt(
    recipeRng.nextInt(band.blockingBias[0], band.blockingBias[1]) +
      ARCHETYPE_BLOCKING_BONUS[archetype] * 4,
    0,
    48,
  );

  // Legal-move targets scale with the arrow count: "a fifth of the board is open" means
  // something different on a 14-arrow board and a 300-arrow board.
  const initialMoveRange = ratioRange(targetArrowCount, band.initialMoveRatioPermille, 2);
  const averageMoveRange = ratioRange(targetArrowCount, band.averageMoveRatioPermille, 3);

  const recipe: LevelRecipe = {
    gridWidth: dimensions.width,
    gridHeight: dimensions.height,
    stencilId: chosen.id,
    stencilCategory: chosen.category,
    rotation,
    mirrorHorizontal,
    mirrorVertical,
    targetArrowCount,
    targetOccupancyPermille: occupancyPermille,
    difficultyBand: bandName,
    archetype,
    targetDependencyDepth,
    initialMoveRange,
    averageMoveRange,
    targetForcedStepRatioPermille: band.forcedStepRatioPermille,
    placementStrategy: strategy,
    directionHeuristics,
    arrowLengthRange,
    blockingBias,
    straightBias: band.straightBias,
    turnBias,
    candidateBudget: band.candidateBudget,
    maxPlacementAttempts: band.maxPlacementAttempts,
    maxRepairIterations: band.maxRepairIterations,
    allowFallback: true,
    signature: [
      bandName,
      archetype,
      `${dimensions.width}x${dimensions.height}`,
      chosen.id,
      `${rotation}${mirrorHorizontal ? 'H' : ''}${mirrorVertical ? 'V' : ''}`,
      occupancyPermille,
    ].join('|'),
  };

  return { recipe, mask, activeCells, targetOccupiedCells, stencilAttempts: attempts };
}

/**
 * Builds the order in which stencils are tried.
 *
 * The fallback order matters as much as the first choice: if two neighbouring levels both
 * fail on their preferred stencil and then walk the manifest in the same fixed order, they
 * land on the same replacement and the campaign visibly repeats itself. Each level
 * therefore gets its own weighted permutation of the remaining candidates.
 */
function orderStencilCandidates(
  eligible: readonly StencilManifestEntry[],
  preferredId: string,
  recent: ReadonlySet<string>,
  band: BandConfig,
  showcase: boolean,
  rng: Pcg32,
): StencilManifestEntry[] {
  const fresh = eligible.filter((entry) => !recent.has(entry.id));
  const stale = eligible.filter((entry) => recent.has(entry.id));
  // Recently used stencils are only reconsidered once every fresh option has failed.
  const pool = fresh.length > 0 ? fresh : eligible;
  const tail = fresh.length > 0 ? stale : [];

  const preferred = pool.find((entry) => entry.id === preferredId);
  const rest = weightedPermutation(
    pool.filter((entry) => entry.id !== preferredId),
    band,
    showcase,
    rng,
  );
  const tailOrder = weightedPermutation(tail, band, showcase, rng);
  return preferred === undefined ? [...rest, ...tailOrder] : [preferred, ...rest, ...tailOrder];
}

/**
 * Moves the silhouettes the neighbouring levels are using to the back of the order.
 *
 * Applied after ordering rather than during it, so the candidate pool — and therefore the
 * prediction that produced this list — is unaffected.
 */
function demoteNeighbourChoices(
  ordered: readonly StencilManifestEntry[],
  neighbours: ReadonlySet<string>,
): StencilManifestEntry[] {
  if (neighbours.size === 0 || ordered.length <= 1) return [...ordered];
  const keep = ordered.filter((entry) => !neighbours.has(entry.id));
  const demoted = ordered.filter((entry) => neighbours.has(entry.id));
  return keep.length > 0 ? [...keep, ...demoted] : [...ordered];
}

/** Deterministic permutation, drawing without replacement in proportion to stencil weight. */
function weightedPermutation(
  entries: readonly StencilManifestEntry[],
  band: BandConfig,
  showcase: boolean,
  rng: Pcg32,
): StencilManifestEntry[] {
  const remaining = [...entries];
  const ordered: StencilManifestEntry[] = [];
  while (remaining.length > 0) {
    const weights = remaining.map((entry) => Math.max(1, stencilWeight(entry, band, showcase)));
    const index = rng.weightedIndex(weights);
    ordered.push(remaining[index]);
    remaining.splice(index, 1);
  }
  return ordered;
}

function pickStrategy(
  archetype: PuzzleArchetype,
  band: BandConfig,
  rng: Pcg32,
): PlacementStrategyId {
  const preferred = ARCHETYPE_STRATEGIES[archetype].filter((strategy) =>
    band.strategies.includes(strategy),
  );
  const pool = preferred.length > 0 ? preferred : band.strategies;
  return rng.pick(pool);
}

function jitterWeights(weights: DirectionHeuristicWeights, rng: Pcg32): DirectionHeuristicWeights {
  const jitter = (value: number): number => Math.max(0, value + rng.nextInt(-1, 1));
  return {
    base: Math.max(1, jitter(weights.base)),
    blocksExisting: jitter(weights.blocksExisting),
    createsDependency: jitter(weights.createsDependency),
    edgeDistance: jitter(weights.edgeDistance),
    directionBalance: jitter(weights.directionBalance),
    localRepetition: jitter(weights.localRepetition),
    towardsInterior: jitter(weights.towardsInterior),
  };
}

/** Converts a permille-of-arrow-count target range into absolute move counts. */
function ratioRange(
  arrowCount: number,
  ratio: readonly [number, number],
  minimum: number,
): readonly [number, number] {
  const low = Math.max(minimum, Math.round((arrowCount * ratio[0]) / 1000));
  const high = Math.max(low + 2, Math.round((arrowCount * ratio[1]) / 1000));
  return [low, high];
}

function filledMask(width: number, height: number): Mask {
  const mask = createMask(width, height);
  mask.cells.fill(1);
  return mask;
}
